import React, { useState, useEffect } from 'react';
import { render } from '@react-email/render';
import { axios } from 'lib/axios';
import { Button } from '@mui/material';
import { Section } from '@react-email/components';
import {
  EmailHtml,
  SimpleEmailFooter,
} from '../components/email/EmailTemplate';
import {
  InviteEmailBody,
  RequestAccessEmailIntro,
  RequestAccessEmailCTA,
  AccessAcceptedEmailBody,
  AccessRejectedEmailIntro,
  OptionalMessageParagraph,
  BuyerSignUpEmailBody,
  ContactUsEmailBody,
  NotificationEmailBody,
} from '../components/email/AuthEmailTemplate';
import RenderReactEmail from '../components/email/RenderReactEmail';

// This page only ever previews/saves the 7 components below (6 bodies + the
// no-unsubscribe footer). It never touches default-head/default-header/default-footer
// or the newsletter bodies, which are already stored via EmailTemplatePreview.js.
const BodyCard = ({ children }) => (
  <Section className="body-section" style={{ padding: '57px 71px', backgroundColor: '#FFF' }}>
    {children}
  </Section>
);

const AuthEmailTemplatePreview = () => {
  const [footerHtml, setFooterHtml] = useState('');
  const [inviteHtml, setInviteHtml] = useState('');
  const [requestAccessHtml, setRequestAccessHtml] = useState('');
  const [accessAcceptedHtml, setAccessAcceptedHtml] = useState('');
  const [accessRejectedHtml, setAccessRejectedHtml] = useState('');
  const [buyerSignUpHtml, setBuyerSignUpHtml] = useState('');
  const [contactUsHtml, setContactUsHtml] = useState('');
  const [notificationHtml, setNotificationHtml] = useState('');

  useEffect(() => {
    const generateTemplates = async () => {
      setFooterHtml(await render(<SimpleEmailFooter />));

      const wrapperHtml = await render(
        <Section className="body-section" style={{ padding: '57px 71px', backgroundColor: '#FFF' }}>__BODY_CONTENT_PLACEHOLDER__</Section>
      );
      const wrapBody = (contentHtml) => wrapperHtml.replace('__BODY_CONTENT_PLACEHOLDER__', contentHtml);

      // --- Invite email ---
      const inviteContentHtml = await render(
        <InviteEmailBody inviteeName="{{.InviteeName}}" companyName="{{.CompanyName}}" url="{{.URL}}" />
      );
      setInviteHtml(wrapBody(inviteContentHtml));

      // --- Request access email (optional Message) ---
      const requestAccessIntroHtml = await render(
        <RequestAccessEmailIntro adminName="{{.AdminName}}" requesterName="{{.RequesterName}}" companyName="{{.CompanyName}}" />
      );
      const requestAccessMessageHtml = await render(<OptionalMessageParagraph message="{{.Message}}" />);
      const requestAccessCTAHtml = await render(<RequestAccessEmailCTA url="{{.URL}}" />);
      const requestAccessContentHtml = `${requestAccessIntroHtml}{{if .Message}}${requestAccessMessageHtml}{{end}}${requestAccessCTAHtml}`;
      setRequestAccessHtml(wrapBody(requestAccessContentHtml));

      // --- Access accepted email ---
      const accessAcceptedContentHtml = await render(
        <AccessAcceptedEmailBody userName="{{.UserName}}" companyName="{{.CompanyName}}" url="{{.URL}}" />
      );
      setAccessAcceptedHtml(wrapBody(accessAcceptedContentHtml));

      // --- Access rejected email (optional Message) ---
      const accessRejectedIntroHtml = await render(
        <AccessRejectedEmailIntro userName="{{.UserName}}" companyName="{{.CompanyName}}" />
      );
      const accessRejectedMessageHtml = await render(<OptionalMessageParagraph message="{{.Message}}" />);
      const accessRejectedContentHtml = `${accessRejectedIntroHtml}{{if .Message}}${accessRejectedMessageHtml}{{end}}`;
      setAccessRejectedHtml(wrapBody(accessRejectedContentHtml));

      // --- Buyer sign-up email (internal notification) ---
      const buyerSignUpContentHtml = await render(
        <BuyerSignUpEmailBody userName="{{.UserName}}" userEmail="{{.UserEmail}}" phoneNumber="{{.PhoneNumber}}" />
      );
      setBuyerSignUpHtml(wrapBody(buyerSignUpContentHtml));

      // --- Contact us email (internal notification) ---
      const contactUsContentHtml = await render(
        <ContactUsEmailBody name="{{.Name}}" email="{{.Email}}" phone="{{.Phone}}" message="{{.Message}}" />
      );
      setContactUsHtml(wrapBody(contactUsContentHtml));

      // --- In-app notification email ---
      const notificationContentHtml = await render(
        <NotificationEmailBody recipientName="{{.RecipientName}}" title="{{.Title}}" message="{{.Message}}" url="{{.URL}}" />
      );
      setNotificationHtml(wrapBody(notificationContentHtml));
    };
    generateTemplates();
  }, []);

  const handleSave = async () => {
    try {
      const payloads = [
        { name: 'simple-footer', type: 'footer', content: footerHtml },
        { name: 'invite-email-body', type: 'body', content: inviteHtml },
        { name: 'request-access-email-body', type: 'body', content: requestAccessHtml },
        { name: 'access-accepted-email-body', type: 'body', content: accessAcceptedHtml },
        { name: 'access-rejected-email-body', type: 'body', content: accessRejectedHtml },
        { name: 'buyer-signup-email-body', type: 'body', content: buyerSignUpHtml },
        { name: 'contact-us-email-body', type: 'body', content: contactUsHtml },
      ];

      await Promise.all(payloads.map((payload) => axios.post('/api/newsletter/components', payload)));

      alert('Auth & contact templates saved successfully!');
    } catch (error) {
      console.error('Error saving templates:', error);
      alert('Failed to save templates. Check the console for details.');
    }
  };

  const handleSaveNotification = async () => {
    try {
      await axios.post('/api/newsletter/components', {
        name: 'notification-email-body',
        type: 'body',
        content: notificationHtml,
      });

      alert('Notification template saved successfully!');
    } catch (error) {
      console.error('Error saving notification template:', error);
      alert('Failed to save the notification template. Check the console for details.');
    }
  };

  const sections = [
    {
      title: 'Invite Email',
      html: inviteHtml,
      preview: (
        <BodyCard>
          <InviteEmailBody inviteeName="Jane Doe" companyName="Acme AB" url="https://app.delegon.com/auth/accept-invitation?token=abc" />
        </BodyCard>
      ),
    },
    {
      title: 'Request Access Email',
      html: requestAccessHtml,
      preview: (
        <BodyCard>
          <RequestAccessEmailIntro adminName="John Smith" requesterName="Jane Doe" companyName="Acme AB" />
          <OptionalMessageParagraph message="I'd like access to review our active tenders." />
          <RequestAccessEmailCTA url="https://app.delegon.com/app/suppliers/1/users" />
        </BodyCard>
      ),
    },
    {
      title: 'Access Accepted Email',
      html: accessAcceptedHtml,
      preview: (
        <BodyCard>
          <AccessAcceptedEmailBody userName="Jane Doe" companyName="Acme AB" url="https://app.delegon.com/app" />
        </BodyCard>
      ),
    },
    {
      title: 'Access Rejected Email',
      html: accessRejectedHtml,
      preview: (
        <BodyCard>
          <AccessRejectedEmailIntro userName="Jane Doe" companyName="Acme AB" />
          <OptionalMessageParagraph message="This company is not accepting new members at this time." />
        </BodyCard>
      ),
    },
    {
      title: 'Buyer Sign-Up Email',
      html: buyerSignUpHtml,
      preview: (
        <BodyCard>
          <BuyerSignUpEmailBody userName="Jane Doe" userEmail="jane@example.com" phoneNumber="+46 70 123 45 67" />
        </BodyCard>
      ),
    },
    {
      title: 'Contact Us Email',
      html: contactUsHtml,
      preview: (
        <BodyCard>
          <ContactUsEmailBody name="Jane Doe" email="jane@example.com" phone="+46 70 123 45 67" message="I have a question about pricing." />
        </BodyCard>
      ),
    },
    {
      title: 'Notification Email',
      html: notificationHtml,
      saveLabel: 'Save Notification Template',
      onSave: handleSaveNotification,
      preview: (
        <BodyCard>
          <NotificationEmailBody
            recipientName="Jane Doe"
            title="New application: Java developer"
            message={'Acme AB submitted John Smith as a candidate for "Java developer".'}
            url="https://app.delegon.com/app/applications/1"
          />
        </BodyCard>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Auth & Contact Email Template Preview</h1>
        <Button variant="contained" onClick={handleSave}>
          Save Auth & Contact Templates
        </Button>
      </div>
      <p>
        Previews and saves the 6 transactional email bodies (invite, request access, access
        accepted/rejected, buyer sign-up, contact us) plus the shared no-unsubscribe footer.
        This never re-saves the newsletter head/header/footer/body templates from the
        newsletter preview page.
      </p>

      {sections.map((section) => (
        <section key={section.title} style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>{section.title}</h2>
            {section.onSave && (
              <Button variant="contained" onClick={section.onSave}>
                {section.saveLabel}
              </Button>
            )}
          </div>
          <div style={{ border: '1px solid #ccc' }}>
            <RenderReactEmail component={<EmailHtml>{section.preview}</EmailHtml>} />
          </div>
          <details>
            <summary>Template String</summary>
            <pre style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', border: '1px solid #ccc', padding: '10px' }}>
              {section.html}
            </pre>
          </details>
        </section>
      ))}

      <hr style={{ margin: '40px 0' }} />

      <section>
        <h2>Footer (no unsubscribe)</h2>
        <div style={{ border: '1px solid #ccc' }}>
          <RenderReactEmail component={<EmailHtml><SimpleEmailFooter /></EmailHtml>} />
        </div>
        <details>
          <summary>Template String</summary>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', border: '1px solid #ccc', padding: '10px' }}>
            {footerHtml}
          </pre>
        </details>
      </section>
    </div>
  );
};

export default AuthEmailTemplatePreview;
